import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Plus, Trash2, X, ExternalLink, FileText, FileWarning, Loader2, Pencil,
} from "lucide-react";
import adminApi, { STATIC_ORIGIN } from "./adminApi";

const emptyForm = { course_name: "", year: "" };

const ACCEPTED_EXTS = [".pdf", ".doc", ".docx", ".xls", ".xlsx", ".jpg", ".jpeg", ".png"];
const ACCEPTED_ATTR =
  ".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,application/pdf," +
  "application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document," +
  "application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet," +
  "image/jpeg,image/png";

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: CURRENT_YEAR - 1900 + 1 }, (_, i) => CURRENT_YEAR - i); // current year first

// function attachmentUrl(filename) {
//   if (!filename) return null;
//   return `${STATIC_ORIGIN}/images/admissiondocs/${filename}`;
// }

function attachmentUrl(filename) {
  if (!filename) return null;
  const fileName = filename.split(/[\\/]/).pop();

  return `${STATIC_ORIGIN}/images/admissiondocs/${fileName}`;
}

function isPdf(filename) {
  return !!filename && filename.toLowerCase().endsWith(".pdf");
}

export default function ManageGovtApproval() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [courseNames, setCourseNames] = useState([]); // [{ id, course_name }] from tbl_coursename

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = "add new"
  const [form, setForm] = useState(emptyForm);
  const [file, setFile] = useState(null);
  const [filePreviewName, setFilePreviewName] = useState(null);
  const [existingAttachment, setExistingAttachment] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const fileInputRef = useRef(null);

  const [toast, setToast] = useState(null);
  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 2600);
  };

  const fetchCourseNames = async () => {
    try {
      const res = await adminApi.get("/admin/govt-approval/meta/course-names");
      setCourseNames(res.data.courseNames || []);
    } catch (err) {
      console.error("Could not load course name list:", err);
    }
  };

  const fetchItems = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await adminApi.get("/admin/govt-approval");
      setItems(res.data.items || []);
    } catch (err) {
      setError(err.response?.data?.message || "Could not load admission courses.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourseNames();
    fetchItems();
  }, []);

  // ── Selection ────────────────────────────────────────────────────────────
  const toggleSelectAll = () => {
    setSelected(selected.length === items.length ? [] : items.map((i) => i.id));
  };
  const toggleSelectOne = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  // ── Bulk delete ──────────────────────────────────────────────────────────
  const handleBulkDelete = async () => {
    if (selected.length === 0) return;
    const ok = window.confirm(
      `Are you sure you want to delete ${selected.length} selected record(s)? This cannot be undone.`
    );
    if (!ok) return;

    setBulkDeleting(true);
    try {
      await adminApi.post("/admin/govt-approval/bulk-delete", { ids: selected });
      showToast("success", `${selected.length} record(s) deleted.`);
      setSelected([]);
      fetchItems();
    } catch (err) {
      showToast("error", err.response?.data?.message || "Could not delete selected records.");
    } finally {
      setBulkDeleting(false);
    }
  };

  // ── Add / Edit modal ─────────────────────────────────────────────────────
  const openAddModal = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFile(null);
    setFilePreviewName(null);
    setExistingAttachment(null);
    setFormError("");
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setEditingId(item.id);
    setForm({
      course_name: item.course_name != null ? String(item.course_name) : "",
      year: item.year || "",
    });
    setFile(null);
    setFilePreviewName(null);
    setExistingAttachment(item.attachment);
    setFormError("");
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = f.name.toLowerCase().slice(f.name.lastIndexOf("."));
    if (!ACCEPTED_EXTS.includes(ext)) {
      setFormError("Only PDF, DOC, DOCX, XLS, XLSX, JPG, JPEG and PNG files are allowed.");
      return;
    }
    setFormError("");
    setFile(f);
    setFilePreviewName(f.name);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!form.course_name) {
      setFormError("Course Name is required.");
      return;
    }
    if (!form.year) {
      setFormError("Year is required.");
      return;
    }
    if (!editingId && !file) {
      setFormError("A file attachment is required.");
      return;
    }

    const fd = new FormData();
    fd.append("course_name", form.course_name);
    fd.append("year", form.year);
    if (file) fd.append("attachment", file);

    setSaving(true);
    try {
      if (editingId) {
        await adminApi.put(`/admin/govt-approval/${editingId}`, fd);
        showToast("success", "Course updated.");
      } else {
        await adminApi.post("/admin/govt-approval", fd);
        showToast("success", "Course added.");
      }
      setShowModal(false);
      fetchItems();
    } catch (err) {
      setFormError(err.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const allSelected = items.length > 0 && selected.length === items.length;

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 shrink-0 mb-2">
        <div>
          <h3 className="font-display font-bold text-[16px] bg-gradient-to-r from-[#0B2545] to-[#0EA5A4] bg-clip-text text-transparent tracking-tight">
            Manage Govt. Approval
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <AnimatePresence>
            {selected.length > 0 && (
              <motion.button
                initial={{ opacity: 0, x: 8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 8 }}
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
                className="flex items-center gap-1.5 bg-red-50 text-red-600 hover:bg-red-100 text-[13px] font-medium px-3 py-1.5 rounded-lg transition disabled:opacity-60"
              >
                {bulkDeleting ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                Delete Selected ({selected.length})
              </motion.button>
            )}
          </AnimatePresence>
          <button
            onClick={openAddModal}
            className="flex items-center gap-1.5 bg-[#0EA5A4] hover:bg-[#0B7E7D] text-white text-[13px] font-medium px-3 py-1.5 rounded-lg transition shadow-sm"
          >
            <Plus size={15} />
            Add New
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex-1 min-h-0 flex flex-col overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400 text-sm gap-2">
            <Loader2 size={18} className="animate-spin" /> Loading...
          </div>
        ) : error ? (
          <div className="py-16 text-center text-sm text-red-500">{error}</div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center">
            <FileText size={28} className="mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-400">Nothing here yet — click "Add New" to create one.</p>
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-auto">
            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0 z-10 shadow-md">
                    <tr className="text-left text-sm">
                      <th className="th-colorful pl-3 pr-2 py-1.5 w-8 border-b-2 border-r normal-case">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={toggleSelectAll}
                          className="w-4 h-4 rounded border-slate-300 accent-[#0EA5A4] cursor-pointer"
                        />
                      </th>
                      <th className="th-colorful px-3 py-1.5 font-bold uppercase tracking-wide border-b-2 border-r">Course Name</th>
                      <th className="th-colorful px-3 py-1.5 font-bold uppercase tracking-wide border-b-2 border-r">Year</th>
                      <th className="th-colorful px-3 py-1.5 font-bold uppercase tracking-wide border-b-2">File Upload</th>
                    </tr>
                  </thead>
              <tbody>
                <AnimatePresence initial={false}>
                  {items.map((it, i) => (
                    <motion.tr
                      key={it.id}
                      layout
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2, delay: i * 0.02 }}
                      onClick={() => openEditModal(it)}
                      className={`cursor-pointer hover:bg-[#0EA5A4]/30 transition-colors duration-150 border-b border-slate-200 ${
                          selected.includes(it.id) ? "bg-[#0EA5A4]/15" : ""
                        }`}
                    >
                      <td className="pl-3 pr-2 py-2 border-r border-slate-200" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selected.includes(it.id)}
                          onChange={() => toggleSelectOne(it.id)}
                          className="w-4 h-4 rounded border-slate-300 accent-[#0EA5A4] cursor-pointer"
                        />
                      </td>
                      <td className="px-3 py-2 text-slate-800 font-medium border-r border-slate-200">{it.course_label || "—"}</td>
                      <td className="px-3 py-2 text-slate-500 border-r border-slate-200">{it.year || "—"}</td>
                      <td className="px-3 py-2">
                        {it.attachment ? (
                          <a
                            href={attachmentUrl(it.attachment)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1.5 text-[#0EA5A4] hover:underline"
                          >
                            

                            {isPdf(it.attachment) ? (
    <div
      style={{
        border: "0.5px solid #999",
        padding: "7px",
        marginTop: "3px",
        marginBottom: "3px",
        borderRadius: "7px",
      }}
    >
      <i
        style={{ color: "green" }}
        className="fa fa-eye"
        aria-hidden="true"
      />
    </div>
  ) : (
    <div
      style={{
        border: "0.5px solid #999",
        padding: "7px",
        marginTop: "3px",
        marginBottom: "3px",
        borderRadius: "7px",
      }}
    >
      <i
        style={{ color: "green" }}
        className="fa fa-eye"
        aria-hidden="true"
      />
    </div>
  )}
                          </a>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-slate-300 text-xs">
                            <FileWarning size={14} /> None
                          </span>
                        )}
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
              toast.type === "success" ? "bg-[#0B2545] text-white" : "bg-red-600 text-white"
            }`}
          >
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add / Edit modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
            className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 12 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                <h4 className="font-display font-semibold text-[#0B2545]">
                  {editingId ? "Edit Course" : "Add Govt Approval"}
                </h4>
                <button onClick={closeModal} className="text-slate-400 hover:text-slate-700">
                  <X size={18} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                    Course Name <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={form.course_name}
                    onChange={(e) => setForm({ ...form, course_name: e.target.value })}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition"
                  >
                    <option value="">-- Select --</option>
                    {courseNames.map((c) => (
                      <option key={c.id} value={c.id}>{c.course_name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                    Year <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={form.year}
                    onChange={(e) => setForm({ ...form, year: e.target.value })}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition"
                  >
                    <option value="">-- Select --</option>
                    {YEARS.map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                    File Upload <span className="text-red-500">*</span>
                  </label>
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-3 border border-dashed border-slate-300 rounded-lg px-3 py-3 cursor-pointer hover:border-[#0EA5A4] transition"
                  >
                    <div className="w-12 h-12 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
                      <FileText size={18} className="text-slate-300" />
                    </div>
                    <div className="text-xs text-slate-500">
                      {filePreviewName
                        ? filePreviewName
                        : existingAttachment
                        ? "Click to replace attachment"
                        : "Click to choose a file"}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={ACCEPTED_ATTR}
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </div>
                </div>

                {formError && (
                  <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                    {formError}
                  </p>
                )}

                <div className="flex items-center gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 flex items-center justify-center gap-2 bg-[#0B2545] hover:bg-[#143A63] text-white font-medium rounded-lg py-2.5 transition disabled:opacity-60"
                  >
                    {saving ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : editingId ? (
                      <Pencil size={15} />
                    ) : (
                      <Plus size={15} />
                    )}
                    {saving ? "Saving..." : editingId ? "Update" : "Submit"}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2.5 rounded-lg text-sm font-medium text-slate-500 hover:bg-slate-50 transition"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
